import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Page } from '../app/Page.tsx';
import { HouseholdSection } from '../features/settings/HouseholdSection.tsx';
import { PasskeysSection } from '../features/settings/PasskeysSection.tsx';
import { authQueryKey, saveServerLocale, signOut, useAuth } from '../lib/auth.ts';
import { useMirror } from '../lib/live.ts';
import { localSlots, slotName } from '../lib/slots.ts';
import { setTheme, useTheme, type Theme } from '../lib/theme.ts';
import { m } from '../paraglide/messages.js';
import { getLocale, locales, setLocale, type Locale } from '../paraglide/runtime.js';
import { Icon } from '../ui/Icon.tsx';
import { List, ListRow } from '../ui/List.tsx';
import { GroupLabel } from '../ui/SectionHeader.tsx';
import { SegmentedControl } from '../ui/SegmentedControl.tsx';
import './settings.css';

export const Route = createFileRoute('/settings')({ component: SettingsScreen });

const languageNames: Record<Locale, () => string> = { en: m.language_en, de: m.language_de };

/** Switches the UI language (reloads) and tells the server, which writes pushes in it. */
async function changeLocale(locale: Locale) {
  await saveServerLocale(locale).catch(() => undefined);
  await setLocale(locale);
}

function SettingsScreen() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const logout = useMutation({
    mutationFn: signOut,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authQueryKey }),
  });
  const user = auth.data?.user;
  const theme = useTheme();
  const slots = useMirror(localSlots, []);
  const shown = slots?.filter((s) => s.visible) ?? [];
  const staples = useMirror((db) => db.ingredients.filter((e) => e.household.isStaple).count(), []);
  const themeOptions: { value: Theme; label: string }[] = [
    { value: 'system', label: m.theme_system() },
    { value: 'light', label: m.theme_light() },
    { value: 'dark', label: m.theme_dark() },
  ];

  return (
    <Page title={m.title_settings()}>
      <div className="settings">
        {user && (
          <section aria-label={m.settings_account()} className="settings__group">
            <GroupLabel as="h2">{m.settings_group_account()}</GroupLabel>
            <List>
              <ListRow
                lead={
                  <span className="settings__avatar">
                    <Icon name="user" />
                  </span>
                }
                title={`${user.displayName} · ${user.household.name}`}
              />
            </List>
          </section>
        )}
        {user && <HouseholdSection user={user} />}
        <PasskeysSection />
        <section className="settings__group" aria-labelledby="settings-planning">
          <GroupLabel as="h2" id="settings-planning">
            {m.settings_group_planning()}
          </GroupLabel>
          <List>
            <ListRow
              icon="calendar"
              title={m.slots_title()}
              sub={shown.map(slotName).join(', ') || undefined}
              value={slots && m.slots_summary({ shown: shown.length, total: slots.length })}
              link={{ to: '/settings/slots' }}
            />
            <ListRow
              icon="box"
              title={m.staples_title()}
              value={staples}
              link={{ to: '/settings/staples' }}
            />
          </List>
        </section>
        <section className="settings__group" aria-labelledby="settings-notifications">
          <GroupLabel as="h2" id="settings-notifications">
            {m.settings_group_notifications()}
          </GroupLabel>
          <List>
            <ListRow
              icon="bell"
              title={m.notify_title()}
              link={{ to: '/settings/notifications' }}
            />
            <ListRow icon="key" title={m.tokens_title()} link={{ to: '/settings/tokens' }} />
            <ListRow icon="users" title={m.friends_title()} link={{ to: '/settings/friends' }} />
          </List>
        </section>
        {user?.isAdmin && (
          <section className="settings__group" aria-labelledby="settings-ai">
            <GroupLabel as="h2" id="settings-ai">
              {m.settings_group_ai()}
            </GroupLabel>
            <List>
              <ListRow icon="box" title={m.ai_title()} link={{ to: '/settings/ai' }} />
            </List>
          </section>
        )}
        <section className="settings__group" aria-labelledby="settings-app">
          <GroupLabel as="h2" id="settings-app">
            {m.settings_group_app()}
          </GroupLabel>
          <List>
            <ListRow
              icon="globe"
              title={<label htmlFor="settings-language">{m.settings_language()}</label>}
              trailing={
                <select
                  id="settings-language"
                  className="settings__select"
                  value={getLocale()}
                  onChange={(e) => void changeLocale(e.target.value as Locale)}
                >
                  {locales.map((locale) => (
                    <option key={locale} value={locale}>
                      {languageNames[locale]()}
                    </option>
                  ))}
                </select>
              }
            />
            <ListRow icon="info" title={m.whats_new_page()} link={{ to: '/settings/whats-new' }} />
            <ListRow
              icon="book"
              title={m.settings_about()}
              value={m.settings_version({ version: import.meta.env.VITE_APP_VERSION ?? 'dev' })}
            />
          </List>
          <div className="settings__theme">
            <GroupLabel as="h3">{m.settings_theme()}</GroupLabel>
            <SegmentedControl
              label={m.settings_theme()}
              options={themeOptions}
              value={theme}
              onChange={setTheme}
            />
          </div>
        </section>
        <List>
          <ListRow
            title={m.settings_sign_out()}
            danger
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
          />
        </List>
      </div>
    </Page>
  );
}
